import { MutationKey, useMutationState } from '@tanstack/react-query';
import { OnboardingTask, OnboardingTaskKey, OnboardingTaskStatus } from '@/types/api/group/onboarding-task';

/** Key for a mutation that completes an onboarding task, so the task shows as done before the API completes it */
export const onboardingMutationKey = (groupId: string, task: OnboardingTaskKey): MutationKey => [
  'onboarding',
  groupId,
  task,
];

export type OnboardingProgress = {
  /** Tasks whose mutation succeeded here */
  completed: OnboardingTaskKey[];
  /** The sale created in the last step, before the group's default sale is set */
  saleId?: string;
};

/**
 * The onboarding tasks the leader just completed here. The API completes tasks asynchronously, so
 * until the polled group catches up, these show as done right away instead of leaving the step's form.
 */
export function useOnboardingProgress(groupId: string): OnboardingProgress {
  const succeeded = useMutationState({
    filters: { mutationKey: ['onboarding', groupId], status: 'success' },
    select: (mutation) => ({
      task: mutation.options.mutationKey?.[2] as OnboardingTaskKey,
      data: mutation.state.data,
    }),
  });

  const sale = succeeded.find(({ task }) => task === OnboardingTaskKey.CreateSale);

  return {
    completed: succeeded.map(({ task }) => task),
    saleId: (sale?.data as { id: string } | undefined)?.id,
  };
}

/** The tasks each task unlocks, as the API has them */
const UNLOCKS: Partial<Record<OnboardingTaskKey, OnboardingTaskKey[]>> = {
  [OnboardingTaskKey.CreatePayoutAccount]: [OnboardingTaskKey.CreateSale],
};

/** The API's tasks with the ones completed here marked completed, and the tasks they unlock pending */
export function withLocalProgress(tasks: OnboardingTask[], { completed }: OnboardingProgress): OnboardingTask[] {
  const unlocked = completed.flatMap((key) => UNLOCKS[key] ?? []);

  return tasks.map((task) => {
    if (completed.includes(task.key)) return { ...task, status: OnboardingTaskStatus.Completed };
    if (task.status === OnboardingTaskStatus.Disabled && unlocked.includes(task.key)) {
      return { ...task, status: OnboardingTaskStatus.Pending };
    }
    return task;
  });
}
